/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { Locale, defaultLocale, locales } from "../i18n";
import { langCookies, migrateFromLocalStorage } from "../lib/cookieUtils";

// Define the context type
type LanguageContextType = {
  currentLanguage: Locale;
  switchLanguage: (lang: Locale) => void;
  t: (key: string) => string;
};

// Create context with default values
const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: defaultLocale,
  switchLanguage: () => {},
  t: () => "",
});

// Custom provider component
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Get initial language from cookies first, then localStorage (for migration)
  const [currentLanguage, setCurrentLanguage] = useState<Locale>(() => {
    // Try cookies first
    const cookieLanguage = langCookies.getLanguage() as Locale;
    if (cookieLanguage && locales.includes(cookieLanguage)) {
      return cookieLanguage;
    }
    
    // Clean up localStorage if it exists
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      // Migrate to cookie and remove from localStorage
      if (locales.includes(savedLanguage as Locale)) {
        langCookies.setLanguage(savedLanguage as 'ar' | 'en');
      }
      localStorage.removeItem("language");
    }
    
    return defaultLocale;
  });

  // Messages for each language
  const [messages, setMessages] = useState<
    Record<string, Record<string, string>>
  >({
    ar: {},
    en: {},
  });

  // Run migration on mount
  useEffect(() => {
    migrateFromLocalStorage();
  }, []);

  // Load messages on mount
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const arMessages = await import("../messages/ar.json");
        const enMessages = await import("../messages/en.json");

        // Process the imported JSON to flatten it into a Record<string, string>
        const processMessages = (
          messageObj: Record<string, unknown>
        ): Record<string, string> => {
          const flattenedMessages: Record<string, string> = {};

          const processObj = (obj: Record<string, unknown>, prefix = "") => {
            Object.entries(obj).forEach(([key, value]) => {
              const fullKey = prefix ? `${prefix}.${key}` : key;

              if (value && typeof value === "object") {
                processObj(value as Record<string, unknown>, fullKey);
              } else if (typeof value === "string") {
                flattenedMessages[fullKey] = value;
              }
            });
          };

          processObj(messageObj);
          return flattenedMessages;
        };

        setMessages({
          ar: processMessages(arMessages),
          en: processMessages(enMessages),
        });
      } catch (error) {
        console.error("Error loading messages:", error);
      }
    };

    loadMessages();
  }, []);

  // Switch language function
  const switchLanguage = (lang: Locale) => {
    if (locales.includes(lang)) {
      setCurrentLanguage(lang);
      // Set in cookie only
      langCookies.setLanguage(lang as 'ar' | 'en');
      // Clean up localStorage if it exists
      if (localStorage.getItem("language")) {
        localStorage.removeItem("language");
      }

      // Update HTML dir attribute for RTL/LTR
      const isRTL = lang === "ar";
      document.documentElement.dir = isRTL ? "rtl" : "ltr";

      // Update HTML lang attribute
      document.documentElement.lang = lang;

      // Add/remove RTL class to body
      if (isRTL) {
        document.body.classList.add("rtl");
        document.body.classList.remove("ltr");
      } else {
        document.body.classList.remove("rtl");
        document.body.classList.add("ltr");
      }

      // Add a small delay to let CSS transitions complete
      setTimeout(() => {
        // Dispatch a custom event to notify components that language changed
        const event = new CustomEvent("languagechange", {
          detail: { language: lang, isRTL },
        });
        document.dispatchEvent(event);
      }, 50);
    }
  };

  // Apply initial language direction
  useEffect(() => {
    const isRTL = currentLanguage === "ar";
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = currentLanguage;

    if (isRTL) {
      document.body.classList.add("rtl");
      document.body.classList.remove("ltr");
    } else {
      document.body.classList.remove("rtl");
      document.body.classList.add("ltr");
    }

    // Dispatch a custom event to notify components that language is initialized
    setTimeout(() => {
      const event = new CustomEvent("languagechange", {
        detail: { language: currentLanguage, isRTL },
      });
      document.dispatchEvent(event);
    }, 50);
  }, [currentLanguage]);

  // Translation function
  const t = (key: string): string => {
    // Handle null or undefined keys
    if (key === null || key === undefined) {
      return "[missing key]";
    }

    // Common translations fallbacks
    const commonFallbacks: Record<string, Record<string, string>> = {
      ar: {
        // Common actions
        delete: "حذف",
        save: "حفظ",
        cancel: "إلغاء",
        confirm: "تأكيد",
        add: "إضافة",
        edit: "تعديل",
        view: "عرض",
        close: "إغلاق",
        signOut: "تسجيل الخروج",

        // Navigation
        mediaLibrary: "مكتبة الوسائط",
        settings: "الإعدادات",
        changeLanguage: "تغيير اللغة",
        messages: "الرسائل",
        profile: "الملف الشخصي",
        search: "بحث",
        notifications: "الإشعارات",
        language: "اللغة",
        viewAllNotifications: "عرض كل الإشعارات",
      },
      en: {
        // Common actions
        delete: "Delete",
        save: "Save",
        cancel: "Cancel",
        confirm: "Confirm",
        add: "Add",
        edit: "Edit",
        view: "View",
        close: "Close",
        signOut: "Sign Out",

        // Navigation
        mediaLibrary: "Media Library",
        settings: "Settings",
        changeLanguage: "Change Language",
        messages: "Messages",
        profile: "Profile",
        search: "Search",
        notifications: "Notifications",
        language: "Language",
        viewAllNotifications: "View All Notifications",
      },
    };

    // Get the language messages
    const languageMessages = messages[currentLanguage];

    if (!languageMessages) {
      // Check for common fallbacks first
      if (
        currentLanguage in commonFallbacks &&
        key in commonFallbacks[currentLanguage as keyof typeof commonFallbacks]
      ) {
        return commonFallbacks[currentLanguage as keyof typeof commonFallbacks][
          key
        ];
      }
      return `[${key}]`; // Return key in brackets to indicate missing translation
    }

    // First check if the key exists directly (flat structure)
    if (languageMessages[key] !== undefined) {
      const value = languageMessages[key];
      // Ensure we return a string, not an object
      if (typeof value === "string") {
        return value;
      } else {
        // Check for common fallbacks
        if (
          currentLanguage in commonFallbacks &&
          key in
            commonFallbacks[currentLanguage as keyof typeof commonFallbacks]
        ) {
          return commonFallbacks[
            currentLanguage as keyof typeof commonFallbacks
          ][key];
        }
        return `[${key}]`;
      }
    }

    // If not found, try the nested approach (backward compatibility)
    // Split the key by dots to navigate nested objects
    const keys = key.split(".");

    // For backward compatibility, handle direct lookup with key path
    if (
      keys.length > 1 &&
      typeof languageMessages[keys.join(".")] === "string"
    ) {
      return languageMessages[keys.join(".")] as string;
    }

    // Try the fallback language if the current language doesn't have this key
    if (currentLanguage !== "en" && messages["en"] && messages["en"][key]) {
      const fallbackValue = messages["en"][key];
      if (typeof fallbackValue === "string") {
        return fallbackValue;
      }
    }

    // Navigate through the nested objects
    let result: unknown = languageMessages;

    try {
      for (const k of keys) {
        if (!result || typeof result !== "object") {
          // Check for common fallbacks before returning bracketed key
          if (
            currentLanguage in commonFallbacks &&
            key in
              commonFallbacks[currentLanguage as keyof typeof commonFallbacks]
          ) {
            return commonFallbacks[
              currentLanguage as keyof typeof commonFallbacks
            ][key];
          }
          return `[${key}]`; // Return key in brackets if path is invalid
        }
        // Use type assertion with Record<string, unknown> to handle any type of nested value
        result = (result as Record<string, unknown>)[k];
      }
    } catch (error) {
      // Check for common fallbacks on error
      if (
        currentLanguage in commonFallbacks &&
        key in commonFallbacks[currentLanguage as keyof typeof commonFallbacks]
      ) {
        return commonFallbacks[currentLanguage as keyof typeof commonFallbacks][
          key
        ];
      }
      return `[${key}]`;
    }

    // Return the translation or the key if not found
    if (typeof result === "string") {
      return result;
    } else if (
      result &&
      typeof result === "object" &&
      "text" in result &&
      typeof (result as { text: unknown }).text === "string"
    ) {
      // Handle case where result is an object with a text property
      return (result as { text: string }).text;
    } else {
      // Final check for common fallbacks
      if (
        currentLanguage in commonFallbacks &&
        key in commonFallbacks[currentLanguage as keyof typeof commonFallbacks]
      ) {
        return commonFallbacks[currentLanguage as keyof typeof commonFallbacks][
          key
        ];
      }
      return `[${key}]`; // Return key in brackets to indicate missing translation
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export function useLanguage() {
  return useContext(LanguageContext);
}
